/** @jsxImportSource @emotion/react */
import React, { useState, useEffect } from "react";
import { css, useTheme } from "@emotion/react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Container,
  Icon,
  Text,
} from "@coreai/component-library";
import { AnalysisSummaryResult } from "./ComplianceReferenceCard";
import Markdown from "react-markdown";
import "./ResultSection.css";
import rehypeRaw from "rehype-raw";
import { hexToRgba } from "helpers/colors";
import Reminders from "./Reminders";
import { useLanguage } from "LanguageContext";
import RateFeedback from "../../../CommonUi/RateFeedback/RateFeedback";
import { useAnalysis } from "../FormComponents/AnalysisResultContext";
import { SHOW_TOGGLE_DETAILS } from "services/config";
import { useShowToggleDetails } from "hooks/useShowToggleDetails";

interface ResultSectionProps {
  title: string;
  items: AnalysisSummaryResult[];
  bgColor: string;
  icon: JSX.Element;
  openAll: boolean;
  showToggle: boolean;
  analysisType: string;
}

const formatEvaluation = (text: any) => {
  return String(text)
    .replace(/##-1##/g, '<span class="circle-gray"></span>')
    .replace(/##0##/g, '<span class="circle-red"></span>')
    .replace(/##2##/g, '<span class="circle-yellow"></span>')
    .replace(/##3##/g, '<span class="circle-blue"></span>')
    .replace(/##1##/g, '<span class="circle-green"></span>').replace(/(\.\*\*)/g, ".**<br/>")
    .replace(/\n/g,'</p><p class="wrapperClass">');
};

export default function ResultSection(props: Readonly<ResultSectionProps>) {
  const { t } = useLanguage();
  const theme = useTheme();
  const { analysisContextResult } = useAnalysis();
  const rgbColor = hexToRgba(props.bgColor, (props?.bgColor === "#F2F6F8" ? 1 : 0.1));
  const [openReminders, setOpenReminders] = useState<Record<number, boolean>>({});
  const [openItems, setOpenItems] = useState<string[]>([]);
  const { buildRegex } = useShowToggleDetails(analysisContextResult);

  useEffect(() => {
    if (props.items) {
      const showList = props.openAll ? props.items.map((item) => item.title) : [];
      setOpenItems(showList);
    }
  }, [props.openAll, props.items]); // Added props.items to dependency array

  const iconContainerStyles = css`
    background-color: ${props.bgColor};
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  `;

  const iconRedContainerStyles = css`
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

  const accordionTriggerStyle = css`
    display: flex;
    justify-content: flex-start;
    align-items: end;
    text-align: left;
    padding-left: ${theme.spacing["5xl"]};
    font-family: ${theme.typography.heading4_medium.fontFamily};
    font-size: ${theme.typography.heading4_medium.fontSize};
    font-weight: ${theme.typography.heading4_medium.fontWeight};
  `;

  const titleHeader = css`
    margin-bottom: ${theme.spacing["xl"]};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing["xxl"]};
    white-space: nowrap;

    @media (max-width: 768px) {
      flex-direction: column;
      gap: 10px;
    }
  `;

  const titleLeftWrapper = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    @media (max-width: 768px) {
      align-self: flex-start;
    }
  `;

  const iconWrapper = css`
    cursor: pointer;
    margin-right: 28px;

    @media (max-width: 768px) {
      align-self: flex-end;
    }
  `;

  const titleStyles = css`
    font-family: ${theme.typography.heading3_medium.fontFamily};
    font-size: ${theme.typography.heading3_medium.fontSize};
    font-weight: ${theme.typography.heading3_medium.fontWeight};
    color: ${theme.colors.textMedium};
    line-height: 120%;
    
    @media (max-width: 768px) {
      font-size: ${theme.typography.heading4_semiBold.fontSize};
    }
  `;

  const listStyles = css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.md};
    position: relative;
    margin: 0 ${theme.spacing["xl"]} 0 0;
    margin-bottom: ${theme.spacing["xl"]};

    &::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 4px;
      height: 100%;
      background-color: ${props.bgColor === "#F2F6F8" ? "#EAEEF0" : props.bgColor};
    }
  `;

  const itemStyles = css`
    background-color: ${theme.colors.white};
    font-family: ${theme.typography.heading4_medium.fontFamily};
    font-size: ${theme.typography.heading4_medium.fontSize};
    font-weight: ${theme.typography.heading4_medium.fontWeight};
    &:hover {
      background-color: ${theme.colors.white};
      cursor: default;
      transform: none;
    }
  `;

  const containerStyle = () => css`
    display: flex;
    flex-direction: column;
    // background-color: ${rgbColor};
    // padding: ${theme.spacing["4xl"]};
    text-align: left;
    border-radius: ${theme.radius.xs};

    @media (max-width: 768px) {
      padding: 12px;
    }
  `;

  const contentStyles = css`
    margin: 0 28px;
    padding-top: 0;
    p{
      margin-top: 12px;
      padding-left: 24px;
    }
    .circle-red, .circle-yellow, .circle-green, .circle-gray, .circle-blue {
      margin-right: 5px;
    }
    span{
      margin-left: -24px;
    }
    .wrapperClass {
     padding-left: 24px;
     margin-top: 12px;
    }
  `;

  const botStyle = css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: row;
    gap: ${theme.spacing.md};
  `;

  const botContainerStyle = css`
    flex: 1;
    background-color: ${theme.colors.grey};
    color: ${theme.colors.primary};
    padding: ${theme.spacing.md};
    font-size: ${theme.typography.body_semiBold.fontSize};
    border: 1px solid ${theme.colors.grey};
    display: flex;
    align-items: center;
    gap: ${theme.spacing.md};
    justify-content: flex-start;
  `;

  const resultSectionReminderStyles = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    color: ${theme.colors.primary};
  `;

  const listItemStyle = css`
    font-family: ${theme.typography.body_semiBold.fontFamily};
    font-size: ${theme.typography.body_semiBold.fontSize};
    font-weight: ${theme.typography.body_semiBold.fontWeight};
    color: #000;
    line-height: 16px;
    display: flex;
    align-items: center;

  `;

  const toggleReminder = (index: number) => {
    setOpenReminders((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const handleSectionToggle = (bgColor: any) => {
    if (openItems.length === props.items.length) {
      setOpenItems([]); // collapse all
    } else {
      setOpenItems(props.items.map((item) => item.title)); // expand all
    }
  };

  const complianceTitles: Record<number, string> = {
    [-1]: t('compliance-1', null, analysisContextResult?.locale_code),
    0: t('compliance0', null, analysisContextResult?.locale_code),
    1: t('compliance1', null, analysisContextResult?.locale_code),
    2: t('compliance2', null, analysisContextResult?.locale_code),
  };
  
  const addComplianceTitlesToAll = (evaluation: string) => {
    return evaluation.replace(
      /(##-?\d##\s*\*\*[A-Za-z0-9]*\d+\.\d+[A-Za-z0-9]*\*\*)/g,
      (match) => {
        const codeMatch = match.match(/##(-?\d)##/);
        if (!codeMatch) return match;
        const complianceValue = Number(codeMatch[1]);
        const title = complianceTitles[complianceValue] || "";
        return `${match} **${title}** </br>`;
      }
    );
  };
    
  const getEmptyListMessage = (anaType: string) => {
    switch (anaType) {
      case "invalid":
        return <div>{t("response_list_empty_wrongs", null, analysisContextResult?.locale_code)}</div>
      case "middle":
        return <div>{t("response_list_empty_corrects", null, analysisContextResult?.locale_code)}</div>;
      case "valid":
        return <div>{t("response_list_empty_corrects", null, analysisContextResult?.locale_code)}</div>;
      case "not-applicable":
        return <div>{t("response_list_empty_not_applicable", null, analysisContextResult?.locale_code)}</div>;
      default:
        return <></>;
    }
  };

  return (
    <Container variant="column" css={containerStyle}>
      <div css={titleHeader} id={props.title}>
        <div css={titleLeftWrapper}>
          <div css={props?.bgColor !== theme.colors.red ? iconContainerStyles:iconRedContainerStyles}>{props.icon}</div>
          <Text css={titleStyles} style={{ marginTop: "6px" }}>
            {props.title}
          </Text>
        </div>
        {props.items.length > 0 && 
          <div css={iconWrapper} className="toggleSection" onClick={() => handleSectionToggle(props?.bgColor)}>
            {(openItems.length === props.items.length) ? (
                <Icon name="chevron-up" size="sm" />
            ) : (
                <Icon name="chevron-down" size="sm" />
            )}
          </div>
        }
      </div>

      {
        (props?.items?.length === 0) ? 
        <>
        {getEmptyListMessage(props?.analysisType)}
        </>
        :
        <div css={listStyles}>
          <Accordion type="multiple" style={{ paddingBottom: 0, padding: 0 }}          
            value={openItems} // Control the open state
            onValueChange={setOpenItems} // Update when user interacts
          >
            {props.items.map((item, index) => {
            let withTitle = addComplianceTitlesToAll(item.evaluation);
            let formattedEvaluation = formatEvaluation(withTitle);
            const isReminderOpen = openReminders[index] || false;
              let showReminderIcon = isReminderOpen ? (
                <Icon name="minus" size="sm" />
              ) : (
                <Icon name="plus" size="sm" />
              );
              if(!props?.showToggle){
                const regex = buildRegex();
                if (regex) {
                  let showDes = item.evaluation.replace(regex, "$1");
                  showDes = addComplianceTitlesToAll(showDes);
                  formattedEvaluation = formatEvaluation(showDes);
                }
              }
              // ** add \n for new line
              let listItemWithNoEvoluation: any[] = [] ;
              if(item?.evaluation === ''){
                listItemWithNoEvoluation = item?.content ? [...item.content.matchAll(/\*\*(.*?)\*\*/g)].map(m => m[1]) : [];
              }
              return (
                <AccordionItem
                  value={item.title}
                  key={`${item.title}-${index}`}
                  css={itemStyles}
                >
                  <AccordionTrigger id={item?.title} css={accordionTriggerStyle}>
                    {!item.title.startsWith("frameworks_") && !item.title.includes("title_principle_") && (
                    item?.index ? `${item.index}. ` : ""
                    )}

                    {item.title.startsWith("frameworks_") && item.title.includes("title_principle_")
                    ? t(item.title, null, analysisContextResult?.locale_code)
                    : item.title}
                  </AccordionTrigger>
                  <AccordionContent css={contentStyles}>
                    {listItemWithNoEvoluation?.length > 0 ? 
                      <>
                        {listItemWithNoEvoluation?.map((_item) => 
                          <p css={listItemStyle}>
                            <span className="circle-gray"></span> {_item} {complianceTitles['-1']}
                          </p>)
                        }
                      </> :
                      <Markdown rehypePlugins={[rehypeRaw]}>
                        {formattedEvaluation}
                      </Markdown>
                    }
                    <div css={botStyle}>
                      <div
                        css={botContainerStyle}
                        onClick={() => toggleReminder(index)}
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          {showReminderIcon}
                        </div>
                        <div css={resultSectionReminderStyles}>
                          {t("analyse_result_section_reminder", null, analysisContextResult?.locale_code)}
                        </div>
                      </div>
                    </div>
                    {isReminderOpen ?  <Reminders title={item?.title} content={item?.content || ''}/> : null}
                    {<RateFeedback title={t("feedback_buttonText", null, analysisContextResult?.locale_code)} feedbackPosition="Analysis" id={item?.id} entityId={item?.id} entityType="response_id" feedbackDetails={item?.feedbacks[0]}/>}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>
      }
    </Container>
  );
}
